import { OutDealDTO } from "@app/bf-api";
import { findPlatformContent } from "@app/api/wp-page-fetcher/utils/platform-content-array";
import { getPlatformAnchorId } from "@app/api/wp-page-fetcher/utils/schema-urls";
import { getGtinProperty } from "@app/api/wp-page-fetcher/utils/get-gtin-property";
import { DealOfferNode, DealProductNode } from "@app/api/wp-page-fetcher/utils/deal-node-properties";
import {
  getSchemaAvailability,
  SCHEMA_ITEM_CONDITION,
  toSchemaPrice
} from "@app/api/wp-page-fetcher/utils/schema-offer-values";

/**
 * The Product node for a single deal tile, with its Offer, both anchored on the deal id so a tile
 * keeps one identity across the pages it appears on. Missing values are left out rather than
 * filled in, so the markup never claims a price, stock level or identifier that does not exist.
 *
 * The node is not serialised here. A deal can be shown in several sections of one page, and two
 * blocks with the same `@id` are merged by Google into one entity with every property doubled
 * ("Dubbel veld brand" in Search Console). The page composes one `@graph` from all its deals and
 * merges repeats by `@id`, see `buildPageStructuredData`. The return type is built from the
 * property lists in `deal-node-properties.ts`: add a property there first, or this will not compile.
 *
 * Note that a deal is one shop's listing, not a product: two shops selling the same item are two
 * deals, and each therefore yields its own Product node carrying the same GTIN. BF-5078 prescribes
 * the deal id as the anchor, so that is what this does.
 *
 * Not every endpoint serialises `categories`: `/deals` and `/deals/bulk` do, `/search` did not
 * until the API side of BF-5067. The merge in `buildPageStructuredData` takes `category` from
 * whichever copy has it. `priceValidUntil`, `review` and `aggregateRating` have no source at deal
 * level.
 */
export const getDealStructuredData = (deal: OutDealDTO, platformId: number): DealProductNode => {
  const price = toSchemaPrice(deal.price);
  const brandName = deal.brands?.[0]?.name;
  const category = deal.categories?.[0]?.name;
  const sellerName = deal.merchant?.name;

  // Every price on a platform is in that platform's currency, so it stands in for a missing one.
  const priceCurrency = deal.currency || findPlatformContent(platformId).currency;

  const offers: DealOfferNode = {
    "@type": "Offer",
    "@id": getPlatformAnchorId(platformId, `offer-${deal.id}`),
    url: deal.url || undefined,
    ...(price ? { price, priceCurrency } : {}),
    availability: getSchemaAvailability(deal.stock),
    itemCondition: SCHEMA_ITEM_CONDITION,
    ...(sellerName ? { seller: { "@type": "Organization", name: sellerName } } : {})
  };

  return {
    "@type": "Product",
    "@id": getPlatformAnchorId(platformId, `product-${deal.id}`),
    name: deal.name,
    // A deal carries only its own title — there is no descriptive text on it — so that title is
    // reused here rather than left empty.
    description: deal.name,
    image: deal.image?.url || undefined,
    ...getGtinProperty(deal.ean),
    // A genuine CMS SKU only — the EAN is a different kind of identifier and belongs under gtin.
    sku: deal.sku || undefined,
    ...(brandName ? { brand: { "@type": "Brand", name: brandName } } : {}),
    ...(category ? { category } : {}),
    offers
  };
};
